import { useEffect, useRef, useState } from 'react'

type UseExitIntentOptions = {
  enabled?: boolean
  mobileDelayMs?: number
}

/**
 * Fires once per session:
 * - Desktop: cursor leaves the top of the viewport.
 * - Mobile/touch: fast upward scroll or an idle delay.
 */
export function useExitIntent({
  enabled = true,
  mobileDelayMs = 18000,
}: UseExitIntentOptions = {}) {
  const [triggered, setTriggered] = useState(false)
  const hasFired = useRef(false)

  useEffect(() => {
    if (!enabled || hasFired.current) return

    const fire = () => {
      if (hasFired.current) return
      hasFired.current = true
      setTriggered(true)
    }

    const onMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget) return
      if (event.clientY > 8) return
      fire()
    }

    let lastScrollY = window.scrollY
    const onScroll = () => {
      const current = window.scrollY
      if (lastScrollY - current > 90 && current < 220) fire()
      lastScrollY = current
    }

    const isTouch = window.matchMedia('(hover: none)').matches
    let timer: number | undefined

    document.addEventListener('mouseout', onMouseOut)
    if (isTouch) {
      window.addEventListener('scroll', onScroll, { passive: true })
      timer = window.setTimeout(fire, mobileDelayMs)
    }

    return () => {
      document.removeEventListener('mouseout', onMouseOut)
      window.removeEventListener('scroll', onScroll)
      if (timer) window.clearTimeout(timer)
    }
  }, [enabled, mobileDelayMs])

  const reset = () => setTriggered(false)

  return { triggered, reset }
}
